import { INSIGHT_TYPE_TO_LABEL } from "@/constants";
import { useCappedList } from "@/hooks/useCappedList";
import { FilterOptions } from "@/types/FilterOptions";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { Button } from "@components/ui/button";
import PageInsightTypes from "@components/SearchFilter/partials/PageInsightTypes";
import { ButtonGroup } from "@components/ui/button-group";
import { i18n } from "@lingui/core";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
} from "@components/ui/command";
import { LightbulbIcon, XIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";

export default function InsightTypes({
  options,
  onChange,
  value,
}: {
  onChange?: (value: SearchFilterV4, clear?: boolean) => void;
  value: SearchFilterV4["insight_types"];
  options: FilterOptions["filter_insight_types"];
}) {
  const { t } = useLingui();

  // Helpers
  const formatCappedList = useCappedList();
  const isArray = Array.isArray(value);
  const valueLabel = value
    ? isArray
      ? formatCappedList(
          value.map((insightType) =>
            i18n._(INSIGHT_TYPE_TO_LABEL[insightType])
          ),
          { cap: 2 }
        )
      : i18n._(INSIGHT_TYPE_TO_LABEL[value])
    : "N/A";

  return (
    <ButtonGroup>
      <Button
        size="xs"
        render={<div />}
        variant={onChange ? "outline" : "secondary"}
        className="pointer-events-none"
      >
        <LightbulbIcon />
        <Trans>Insikter</Trans>
      </Button>
      <Button
        size="xs"
        render={<div />}
        variant={onChange ? "outline-muted" : "secondary"}
        className="pointer-events-none"
      >
        {isArray && value.length > 1 ? (
          <Trans>är någon av</Trans>
        ) : (
          <Trans>är</Trans>
        )}
      </Button>
      {onChange ? (
        <Popover>
          <PopoverTrigger render={<Button size="xs" variant="outline" />}>
            {valueLabel}
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-0">
            <Command>
              <CommandInput placeholder={t`Insikter`} />
              <CommandList>
                <CommandEmpty>
                  <Trans>Inga filter hittades</Trans>
                </CommandEmpty>
                <PageInsightTypes
                  value={value}
                  onChange={onChange}
                  options={options}
                />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : (
        <Button
          size="xs"
          render={<div />}
          variant="secondary"
          className="pointer-events-none"
        >
          {valueLabel}
        </Button>
      )}
      {onChange && (
        <Button
          size="icon-xs"
          variant="outline"
          type="button"
          onClick={() => {
            onChange({
              insight_types: undefined,
            });
          }}
        >
          <XIcon />
          <span className="sr-only">
            <Trans>Ta bort filter</Trans>
          </span>
        </Button>
      )}
    </ButtonGroup>
  );
}
